import React from 'react';
import { FolderSection } from '../FolderSection';
import { CodeSetItem } from '../CodeSetItem';

interface SectionContentProps {
  sectionData: any;
  expandedFolder: string | null;
  toggleFolder: (folderId: string) => void;
  getCodeSetInstanceCount: (itemId: string) => number;
  handleItemSelection: (item: any) => void;
  allowMultiple: boolean;
  isLimitReached: boolean;
  handleModeToggle?: (item: any, mode: 'treating' | 'sending') => void;
  getActiveModesForCodeSet?: (itemId: string) => ('treating' | 'sending')[];
  isReferringDisabled?: boolean;
}

/**
 * SectionContent component - Renders folders and ungrouped items for a section
 */
export const SectionContent: React.FC<SectionContentProps> = ({
  sectionData,
  expandedFolder,
  toggleFolder,
  getCodeSetInstanceCount,
  handleItemSelection,
  allowMultiple,
  isLimitReached,
  handleModeToggle,
  getActiveModesForCodeSet,
  isReferringDisabled = false,
}) => {
  const hasFolders = sectionData?.folders?.length > 0;
  const hasUngroupedItems = sectionData?.ungroupedItems?.length > 0;
  const isEmpty = !hasFolders && !hasUngroupedItems;

  if (isEmpty) {
    return (
      <div className={`px-4 py-2 flex items-center gap-2 ${allowMultiple ? 'col-span-full' : 'group self-stretch'}`}>
        <div className="flex-1 justify-start text-neutral-500 pl-7 text-base font-normal font-['Inter'] leading-normal">
          No profiles available
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Folders */}
      {sectionData?.folders?.map((folder: any, index: number) => (
        <FolderSection
          key={`${folder.id}-${index}`}
          folder={folder}
          isExpanded={expandedFolder === folder.id?.toString()}
          onToggle={toggleFolder}
          getCodeSetInstanceCount={getCodeSetInstanceCount}
          handleItemSelection={handleItemSelection}
          allowMultiple={allowMultiple}
          isLimitReached={isLimitReached}
          handleModeToggle={handleModeToggle}
          getActiveModesForCodeSet={getActiveModesForCodeSet}
          isReferringDisabled={isReferringDisabled}
        />
      ))}

      {/* Ungrouped Items */}
      {sectionData?.ungroupedItems?.map((item: any, index: number) => {
        const instanceCount = getCodeSetInstanceCount(item.id);
        const isMaxedOut = allowMultiple && instanceCount >= 2;
        const isDisabled = isLimitReached && instanceCount === 0;

        return (
          <CodeSetItem
            key={`${item.id}-${index}`}
            item={item}
            instanceCount={instanceCount}
            isMaxedOut={isMaxedOut}
            isDisabled={isDisabled}
            onSelect={handleItemSelection}
            allowMultiple={allowMultiple}
            onModeToggle={handleModeToggle}
            activeModes={getActiveModesForCodeSet?.(item.id) || []}
            isLimitReached={isLimitReached}
            isReferringDisabled={isReferringDisabled}
          />
        );
      })}
    </>
  );
};

export default SectionContent;
